// Fake but realistic-looking analysis preview for free tier
// Shows the full UI but with blurred/locked results and a clear upgrade CTA

interface FakeResult {
  grade: string
  gradeColor: string
  direction: string
  dirColor: string
  confidence: number
  entryZone: string
  stopLoss: string
  target1: string
  target2: string
  riskReward: string
  momentum: string
  summary: string
  modules: { icon: string; label: string; value: string }[]
}

const FAKE_CHART: FakeResult = {
  grade:      'B+',
  gradeColor: '#a8d8a0',
  direction:  'LONG',
  dirColor:   'var(--cash)',
  confidence: 74,
  entryZone:  '$187.20 – $188.50',
  stopLoss:   '$184.80',
  target1:    '$193.40',
  target2:    '$197.60',
  riskReward: '1 : 2.4',
  momentum:   'MODERATE',
  summary:    'Price has swept sell-side liquidity below the previous swing low and is showing early signs of reversal. A bullish order block at $187–188 aligns with the 61.8% retracement of the most recent impulse, providing a high-probability OTE entry. HTF remains bullish with price trading in discount.',
  modules: [
    { icon: '/icons/market_structure.svg', label: '1 · Market Structure',    value: 'Bullish BOS confirmed on H4. LTF CHoCH printed at 187.20 signaling potential reversal from LH/LL sequence.' },
    { icon: '/icons/order_block.svg', label: '2 · Order Blocks',        value: 'Bullish OB identified at $186.90–$188.20 — last bearish candle before displacement. Acting as demand zone.' },
    { icon: '⬜', label: '3 · Fair Value Gaps',      value: 'Open bullish FVG at $187.80–$189.10. Price yet to fill imbalance. Magnetic target on pullback.' },
    { icon: '/icons/liquidity.svg', label: '4 · Liquidity Pools',     value: 'Equal lows swept at $184.80 — sell-side liquidity taken. Buy-side resting above $194.20 previous highs.' },
    { icon: '/icons/market_structure.svg', label: '5 · BOS / CHoCH',         value: 'H1 CHoCH confirmed above $189.40. Previous structure broken to upside. Momentum shifting bullish.' },
    { icon: '/icons/ote.svg', label: '6 · Premium & Discount',  value: 'DISCOUNT — price trading below 50% equilibrium of recent swing. Favorable long positioning zone.' },
    { icon: '/icons/ote.svg', label: '7 · Optimal Trade Entry',  value: 'OTE at 61.8%–79% Fib ($187.20–$188.80) aligning with OB and FVG confluence. High probability entry zone.' },
    { icon: '/icons/macro.svg', label: '13 · MTF Confluence',     value: 'D1 bullish, H4 bullish BOS, H1 CHoCH — all timeframes aligning long. Strong institutional narrative.' },
  ],
}

const FAKE_OPTIONS: FakeResult = {
  grade:      'C+',
  gradeColor: 'var(--amber)',
  direction:  'WAIT',
  dirColor:   'var(--amber)',
  confidence: 42,
  entryZone:  '$195 calls / $190 puts',
  stopLoss:   'Exit at 50% loss',
  target1:    '200% gain target',
  target2:    'Roll at 21 DTE',
  riskReward: '1 : 3.0',
  momentum:   'WEAK',
  summary:    'IV rank is elevated at 68 — options are expensive. Max pain sits at $192.50, well above current price. Call skew is slightly positive but put OI dominates near-term expiry suggesting institutional hedging. Wait for IV crush before entering long premium.',
  modules: [
    { icon: '/icons/options.svg', label: 'IV & Skew',         value: 'IV Rank 68 — elevated. Call/Put IV skew +2.3% favoring upside. Expensive to buy premium in this environment.' },
    { icon: '/icons/warning.svg', label: 'Max Pain',           value: 'Max pain at $192.50. Market makers positioned to pin price near this level through expiration Friday.' },
    { icon: '/icons/order_block.svg', label: 'Gamma Walls',        value: 'Major call wall at $195 (42k OI), put wall at $185 (38k OI). Price likely to range between these levels.' },
    { icon: '/icons/flow.svg', label: 'Options Flow',       value: 'Unusual call sweep detected at $200 strike — 2,400 contracts at ask. Bullish positioning for next 30 days.' },
    { icon: '/icons/ote.svg', label: 'Put/Call Ratio',     value: 'PCR: 0.87 — slightly bullish. Near-term put OI elevated at current strikes suggesting hedging activity.' },
    { icon: '/icons/earnings.svg', label: 'Expiry Analysis',    value: 'Highest OI at this Friday expiry. Watch for pinning behavior toward $192–$193 into close.' },
  ],
}

interface Props {
  type: 'chart' | 'options'
  onUpgrade: () => void
}

export default function FreePreview({ type, onUpgrade }: Props) {
  const fake   = type === 'chart' ? FAKE_CHART : FAKE_OPTIONS
  const dc     = fake.direction.replace(/\s+/g, '').toUpperCase()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Demo banner */}
      <div style={{
        background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.3)',
        borderRadius: 3, padding: '10px 14px',
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <img src="/icons/watchlist.svg" alt="" style={{width:14,height:14,verticalAlign:'middle'}}/>
        <div style={{ flex: 1, fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--amber)', lineHeight: 1.5 }}>
          <strong>Preview mode</strong> — this is a sample analysis to show you what you get.
          Upgrade to run real AI analysis on your own charts.
        </div>
        <button onClick={onUpgrade} style={{
          padding: '6px 16px', background: 'var(--cash)', color: 'var(--ink)',
          border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
          fontSize: '.72rem', cursor: 'pointer', whiteSpace: 'nowrap',
        }}>
          Upgrade — $9.99/mo →
        </button>
      </div>

      {/* Grade + direction row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '3.8rem', lineHeight: 1, color: fake.gradeColor }}>
          {fake.grade}
        </div>
        <div style={{
          padding: '6px 14px', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
          fontSize: '1rem', letterSpacing: '.06em', textTransform: 'uppercase',
          background: dc === 'LONG' ? 'rgba(133,196,124,.12)' : dc === 'SHORT' ? 'rgba(224,92,92,.12)' : 'rgba(212,168,67,.12)',
          color: fake.dirColor,
          border: `1px solid ${fake.dirColor}`,
        }}>
          {fake.direction}
        </div>
        <div style={{ marginLeft: 'auto', fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>
          DEMO · {fake.confidence}% confidence
        </div>
      </div>

      {/* Trade levels grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, position: 'relative' }}>
        {[
          { label: 'Entry Zone',   val: fake.entryZone,   color: '' },
          { label: 'Stop Loss',    val: fake.stopLoss,    color: 'var(--red)' },
          { label: 'Target 1',     val: fake.target1,     color: 'var(--cash)' },
          { label: 'Target 2',     val: fake.target2,     color: 'var(--cash)' },
          { label: 'Risk/Reward',  val: fake.riskReward,  color: '' },
          { label: 'Momentum',     val: fake.momentum,    color: 'var(--amber)' },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 3, padding: '8px 11px' }}>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 2 }}>{label}</div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.75rem', color: color || 'var(--text)', filter: 'blur(4px)', userSelect: 'none' }}>{val}</div>
          </div>
        ))}
        {/* Overlay lock */}
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 8, background: 'rgba(10,10,10,.5)',
          borderRadius: 3, backdropFilter: 'blur(1px)',
        }}>
          <img src="/icons/lock.svg" alt="" style={{width:15,height:15,verticalAlign:'middle'}}/>
          <button onClick={onUpgrade} style={{
            padding: '7px 20px', background: 'var(--cash)', color: 'var(--ink)',
            border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
            fontSize: '.75rem', cursor: 'pointer',
          }}>Unlock with Pro</button>
        </div>
      </div>

      {/* ICT modules — first 3 visible, rest blurred */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {fake.modules.map((m, i) => (
          <div key={m.label} style={{
            background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 3,
            padding: '8px 11px', display: 'flex', gap: 8, alignItems: 'flex-start',
            filter: i >= 3 ? 'blur(3px)' : 'none',
            userSelect: i >= 3 ? 'none' : 'auto',
            opacity: i >= 3 ? 0.6 : 1,
            transition: 'filter .2s',
          }}>
            <span style={{ fontSize: '.72rem', opacity: .65, flexShrink: 0, marginTop: 2 }}>{m.icon}</span>
            <div style={{ fontSize: '.72rem', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--cash)', fontFamily: 'Syne Mono', fontSize: '.62rem' }}>{m.label}: </strong>
              <span>{m.value}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Summary — blurred */}
      <div style={{
        background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)',
        borderLeft: '3px solid var(--cash)', borderRadius: 2, padding: '11px 13px',
        position: 'relative', overflow: 'hidden',
      }}>
        <p style={{ fontSize: '.76rem', lineHeight: 1.7, opacity: .9, filter: 'blur(3px)', userSelect: 'none' }}>
          {fake.summary}
        </p>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: 'rgba(10,10,10,.6)',
        }}>
          <button onClick={onUpgrade} style={{
            padding: '8px 22px', background: 'transparent',
            border: '1px solid var(--cash)', borderRadius: 2,
            color: 'var(--cash)', fontFamily: 'Syne', fontWeight: 800,
            fontSize: '.75rem', cursor: 'pointer',
          }}>
            <span style={{display:'flex',alignItems:'center',gap:6}}><img src="/icons/lock.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> Upgrade to read full analysis</span>
          </button>
        </div>
      </div>

      {/* Feature comparison */}
      <div style={{
        background: 'var(--card)', border: '1px solid var(--edge)',
        borderRadius: 3, padding: '16px',
      }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.82rem', color: 'var(--bill)', marginBottom: 12 }}>
          What you unlock with Pro
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {[
            { icon: '/icons/analyze.svg', text: 'Real AI analysis on any chart you upload' },
            { icon: '/icons/market_structure.svg', text: 'All 14 ICT/SMC modules analyzed' },
            { icon: '/icons/ote.svg', text: 'Entry zones, stop loss, targets with R:R ratio' },
            { icon: '/icons/history.svg', text: 'Unlimited analysis history' },
            { icon: '/icons/options.svg', text: 'Options chain analysis from screenshots' },
            { icon: '/icons/flow.svg', text: 'Trade visualization chart' },
          ].map(f => (
            <div key={f.text} style={{ display: 'flex', gap: 10, alignItems: 'center', fontFamily: 'Syne Mono', fontSize: '.64rem', color: 'var(--muted)' }}>
              <span>{f.icon}</span>
              <span>{f.text}</span>
            </div>
          ))}
        </div>
        <button onClick={onUpgrade} style={{
          width: '100%', marginTop: 14, padding: '11px',
          background: 'var(--cash)', color: 'var(--ink)',
          border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800,
          fontSize: '.82rem', cursor: 'pointer', letterSpacing: '.04em',
        }}>
          Get Pro — $9.99/mo →
        </button>
        <div style={{ textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--muted)', marginTop: 8, opacity: .6 }}>
          Cancel anytime
        </div>
      </div>
    </div>
  )
}
